import { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { doc, collection, onSnapshot } from 'firebase/firestore';
import { db } from '../../firebase';
import { useAuth } from '../../contexts/AuthContext';
import { usePreviewNavigate } from '../../contexts/PreviewContext';
import { inPortals } from '../../contexts/MarketContext';
import { useT } from '../../contexts/LocaleContext';
import { CATEGORY_STYLES, CATEGORY_DOT } from '../../utils/statuses';
import { totalTravellers } from '../../utils/groupSize';
import FullItineraryBuilder from '../../components/FullItineraryBuilder';
import SkeletalItineraryView from '../../components/SkeletalItineraryView';
import TeamFixturesSection from '../../components/TeamFixtures';
import SquadCard from '../../components/SquadCard';
import TourNotes from '../../components/TourNotes';
import { tourLeadersOf, isPlayer, playerDetails } from '../../components/TeamsTab';
import useTourTeams from '../../hooks/useTourTeams';
import { localAgeLabel } from '../../utils/teamFixtures';

// Everything here is read-only: football operations can see a tour's football
// side but never change it. Labels are footballOps.tab<Name> strings.

// `allowed` reads the user's access settings (utils/footballOpsPermissions).
const TABS = [
  { key: 'overview',  label: 'tabOverview',  allowed: (p) => p.overview || p.groupContact },
  { key: 'fixtures',  label: 'tabFixtures',  allowed: (p) => p.fixtures },
  { key: 'team_info', label: 'tabTeamInfo',  allowed: (p) => p.teamInfo },
  { key: 'teams',     label: 'tabTeams',     allowed: (p) => p.teams },
  { key: 'itinerary', label: 'tabItinerary', allowed: (p) => p.itinerary },
  { key: 'skeleton',  label: 'tabSkeleton',  allowed: (p) => p.skeleton },
  { key: 'notes',     label: 'tabNotes',     allowed: (p) => p.notes },
];

function formatDate(str) {
  if (!str) return '—';
  const [y, m, d] = str.split('-');
  return `${d}/${m}/${y}`;
}

function Field({ label, value }) {
  return (
    <div>
      <p className="text-[11px] font-semibold text-[#9CA3AF] uppercase tracking-[0.9px] mb-0.5">{label}</p>
      <p className="text-[13px] text-[#111827]">{value || '—'}</p>
    </div>
  );
}

function Card({ title, children }) {
  return (
    <div className="bg-white rounded-[14px] border border-[#E5E7EB] shadow-[0_1px_2px_rgba(16,24,40,.04)] p-5">
      {title && <p className="text-[10px] font-bold text-[#9CA3AF] uppercase tracking-[0.9px] mb-3">{title}</p>}
      {children}
    </div>
  );
}

function Overview({ request, perms }) {
  const t = useT();
  const fl = request.flightDetails || {};
  const hasFlights = fl.airline || fl.outboundFlight || fl.returnFlight;
  const leaders = tourLeadersOf(request);
  return (
    <div className="space-y-4">
      {perms.overview && (<>
      <Card title={t('footballOps.secTourDetails')}>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
          <Field label={t('footballOps.fSport')}        value={t.value('sport', request.sport)} />
          <Field label={t('footballOps.fDestination')}  value={request.destination} />
          <Field label={t('footballOps.fHotel')}        value={request.hotelName} />
          <Field label={t('footballOps.fDeparture')}    value={formatDate(request.departureDate)} />
          <Field label={t('footballOps.fReturn')}       value={formatDate(request.returnDate)} />
          <Field label={t('footballOps.fGroupType')}    value={request.groupType} />
          <Field label={t('footballOps.fTotalTravellers')} value={totalTravellers(request) ? t('footballOps.pax', { n: totalTravellers(request) }) : null} />
          <Field label={t('footballOps.fPlayers')}      value={request.participants ? `${request.participants}` : null} />
          <Field label={t('footballOps.fSquads')}       value={request.numberOfSquads != null ? `${request.numberOfSquads}` : null} />
        </div>
      </Card>

      {hasFlights && (
        <Card title={t('footballOps.secFlights')}>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
            {fl.airline           && <Field label={t('footballOps.fAirline')}            value={fl.airline} />}
            {fl.outboundFlight    && <Field label={t('footballOps.fOutboundFlight')}    value={fl.outboundFlight} />}
            {fl.outboundTime      && <Field label={t('footballOps.fOutboundDeparture')} value={fl.outboundTime} />}
            {fl.arrivalTime       && <Field label={t('footballOps.fArrivalTime')}       value={fl.arrivalTime} />}
            {fl.returnFlight      && <Field label={t('footballOps.fReturnFlight')}      value={fl.returnFlight} />}
            {fl.returnTime        && <Field label={t('footballOps.fReturnDeparture')}   value={fl.returnTime} />}
            {fl.returnArrivalTime && <Field label={t('footballOps.fReturnArrival')}     value={fl.returnArrivalTime} />}
          </div>
        </Card>
      )}

      </>)}

      <Card title={t('footballOps.secPeople')}>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
          {perms.groupContact && (<>
            <Field label={t('footballOps.fGroupContact')} value={request.contactName} />
            <Field label={t('footballOps.fPhone')}        value={request.phone} />
            <Field label={t('footballOps.fEmail')}        value={request.customerEmail} />
          </>)}
          {perms.overview && (
            <Field label={leaders.length > 1 ? t('footballOps.fTourLeaders') : t('footballOps.fTourLeader')} value={leaders.map((l) => l.name).join(', ')} />
          )}
        </div>
      </Card>

      {perms.overview && request.notes && (
        <Card title={t('footballOps.secGroupNotes')}>
          <p className="text-[13px] text-[#374151] whitespace-pre-wrap leading-relaxed">{request.notes}</p>
        </Card>
      )}
    </div>
  );
}

function TeamInfoView({ requestId, request }) {
  const t = useT();
  const [teamInfo, setTeamInfo] = useState(undefined);

  useEffect(() => onSnapshot(
    doc(db, 'requests', requestId, 'teamInfo', 'data'),
    (snap) => setTeamInfo(snap.exists() ? snap.data() : null),
    () => setTeamInfo(null)
  ), [requestId]);

  if (teamInfo === undefined) return <p className="text-[13px] text-[#9CA3AF]">{t('footballOps.loading')}</p>;
  if (!teamInfo) {
    return (
      <div className="bg-white rounded-[14px] border border-[#E5E7EB] p-10 text-center">
        <p className="text-[14px] font-bold text-[#111827] mb-1">{t('footballOps.noTeamInfoTitle')}</p>
        <p className="text-[13px] text-[#6B7280]">{t('footballOps.noTeamInfoDesc')}</p>
      </div>
    );
  }
  return (
    <div className="space-y-4">
      <Card title={t('footballOps.coachTeamInfo')}>
        <div className="grid grid-cols-2 gap-4">
          <Field label={t('footballOps.fSquads')} value={teamInfo.numSquads != null ? `${teamInfo.numSquads}` : null} />
          {teamInfo.goalkeepersTotal != null && <Field label={t('footballOps.fTotalGoalkeepers')} value={`${teamInfo.goalkeepersTotal}`} />}
        </div>
      </Card>
      {(teamInfo.squads || []).map((sq, i) => (
        <SquadCard key={i} squad={sq} index={i} request={request} uidPrefix="fo-kit" />
      ))}
    </div>
  );
}

function fullName(p) {
  return [p.firstName, p.surname].filter(Boolean).join(' ') || '—';
}

// Who is in which team — a roster, not the drag-and-drop board.
function TeamsView({ requestId, request }) {
  const t = useT();
  const { loading, teams, assignments } = useTourTeams(requestId, request, { sync: false });
  const [participants, setParticipants] = useState([]);

  useEffect(() => onSnapshot(
    collection(db, 'requests', requestId, 'participants'),
    (snap) => setParticipants(snap.docs.map((d) => ({ id: d.id, ...d.data() }))),
    () => setParticipants([])
  ), [requestId]);

  const byId = useMemo(() => Object.fromEntries(participants.map((p) => [p.id, p])), [participants]);
  const assigned = new Set(Object.values(assignments).flat());
  const unassignedPlayers = participants.filter((p) => isPlayer(p) && !assigned.has(p.id));

  if (loading) return <p className="text-[13px] text-[#9CA3AF]">{t('footballOps.loading')}</p>;
  if (teams.length === 0) {
    return (
      <div className="bg-white rounded-[14px] border border-[#E5E7EB] p-10 text-center">
        <p className="text-[14px] font-bold text-[#111827] mb-1">{t('footballOps.teamsNotSetTitle')}</p>
        <p className="text-[13px] text-[#6B7280]">{t('footballOps.teamsNotSetDesc')}</p>
      </div>
    );
  }
  return (
    <div className="space-y-4">
      {unassignedPlayers.length > 0 && (
        <p className="text-[12.5px] text-[#92400E] bg-[#FFFBEB] border border-[#FCD34D] rounded-[10px] px-4 py-2.5">
          {unassignedPlayers.length === 1 ? t('footballOps.unplacedOne') : t('footballOps.unplacedMany', { n: unassignedPlayers.length })}
        </p>
      )}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {teams.map((team) => {
          const members = (assignments[team.id] || []).map((pid) => byId[pid]).filter(Boolean)
            .sort((a, b) => fullName(a).localeCompare(fullName(b)));
          return (
            <div key={team.id} className="bg-white rounded-[14px] border border-[#E5E7EB]">
              <div className="px-5 py-3 border-b border-[#F1F3F6]">
                <p className="text-[14px] font-bold text-[#111827]">{team.name}</p>
                {team.ageLabel && <p className="text-[12px] font-semibold text-[#6B7280]">{localAgeLabel(team.ageLabel, t)}</p>}
                <p className="text-[11px] text-[#9CA3AF]">
                  {members.length === 1 ? t('footballOps.playerOne') : t('footballOps.playerMany', { n: members.length })}
                  {team.tourLeaderName && <> · {t('footballOps.fTourLeader')}: <span className="font-semibold text-[#6B7280]">{team.tourLeaderName}</span></>}
                </p>
              </div>
              <div className="px-5 py-3">
                {members.length === 0 ? (
                  <p className="text-[12.5px] text-[#9CA3AF]">{t('footballOps.noPlayersPlaced')}</p>
                ) : (
                  <ul className="space-y-1">
                    {members.map((p) => (
                      <li key={p.id} className="text-[13px] text-[#374151]">
                        {fullName(p)}
                        {playerDetails(p) && <span className="text-[#6B7280]"> ({playerDetails(p)})</span>}
                        {!isPlayer(p) && <span className="text-[11px] text-[#9CA3AF]"> · {p.travellerType}</span>}
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default function FootballOpsTourDetail({ id: idProp } = {}) {
  const params = useParams();
  const id = idProp || params.id;
  const { userPortals, footballOpsPermissions: perms } = useAuth();
  const navigate = usePreviewNavigate();
  const t = useT();
  const [request, setRequest] = useState(undefined);
  const tabs = TABS.filter((tab) => tab.allowed(perms));
  const [chosenTab, setActiveTab] = useState(null);
  const activeTab = tabs.some((tab) => tab.key === chosenTab) ? chosenTab : tabs[0]?.key;

  useEffect(() => onSnapshot(
    doc(db, 'requests', id),
    (snap) => setRequest(snap.exists() ? { id: snap.id, ...snap.data() } : null),
    () => setRequest(null)
  ), [id]);

  if (request === undefined) return <p className="text-[14px] text-[#9CA3AF]">{t('footballOps.loading')}</p>;
  // Tours outside this user's portals are treated as not found.
  if (!request || !inPortals(request, userPortals)) {
    return (
      <div className="max-w-4xl">
        <button onClick={() => navigate('/football-ops')} className="text-[13px] font-semibold text-pb hover:text-navy mb-4">← {t('footballOps.allTours')}</button>
        <p className="text-[14px] text-[#9CA3AF]">{t('footballOps.tourNotFound')}</p>
      </div>
    );
  }

  const cat = request.category || 'sales_admin';
  const sportLabel = t.value('sport', request.sport);
  const title = request.tourName || `${sportLabel} — ${request.destination}`;

  return (
    <div className="max-w-5xl">
      <button onClick={() => navigate('/football-ops')}
        className="flex items-center gap-1 text-[12.5px] font-semibold text-[#6B7280] hover:text-[#111827] mb-3">
        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="15 18 9 12 15 6"/></svg>
        {t('footballOps.allTours')}
      </button>

      <div className="flex flex-wrap items-start justify-between gap-3 mb-4">
        <div className="min-w-0">
          <h2 className="text-[20px] font-bold text-[#111827]">{title}</h2>
          <p className="text-[13px] text-[#6B7280] mt-0.5">
            {request.tourName && <span>{sportLabel} — {request.destination} · </span>}
            {formatDate(request.departureDate)} – {formatDate(request.returnDate)}
            {perms.overview && totalTravellers(request) ? ` · ${t('footballOps.pax', { n: totalTravellers(request) })}` : ''}
          </p>
        </div>
        <span className={`inline-flex items-center gap-1 px-[9px] py-[3px] rounded-[12px] text-[11px] font-semibold whitespace-nowrap ${CATEGORY_STYLES[cat] || 'bg-[#F3F4F6] text-[#374151]'}`}>
          <span className={`w-[5px] h-[5px] rounded-full ${CATEGORY_DOT[cat] || 'bg-[#9CA3AF]'}`} />
          {t(`category.${cat}`)}
        </span>
      </div>

      {/* Tabs */}
      <div data-preview-nav className="flex gap-0 border-b border-[#E5E7EB] mb-5 overflow-x-auto">
        {tabs.map((tab) => (
          <button key={tab.key} onClick={() => setActiveTab(tab.key)}
            className={`px-4 py-2.5 text-[13px] font-semibold border-b-2 -mb-px whitespace-nowrap transition ${
              activeTab === tab.key ? 'border-pb text-pb' : 'border-transparent text-[#6B7280] hover:text-[#374151]'
            }`}>
            {t(`footballOps.${tab.label}`)}
          </button>
        ))}
      </div>

      {tabs.length === 0 && <p className="text-[13px] text-[#9CA3AF]">{t('footballOps.noAccess')}</p>}
      {activeTab === 'overview'  && <Overview request={request} perms={perms} />}
      {activeTab === 'fixtures'  && <TeamFixturesSection requestId={id} request={request} showContacts={perms.fixtureContacts} showItineraryLinks={perms.itinerary} />}
      {activeTab === 'team_info' && <TeamInfoView requestId={id} request={request} />}
      {activeTab === 'teams'     && <TeamsView requestId={id} request={request} />}
      {activeTab === 'itinerary' && <FullItineraryBuilder requestId={id} request={request} readOnly showFixtureContacts={perms.fixtureContacts} />}
      {activeTab === 'skeleton'  && (
        <Card>
          <SkeletalItineraryView requestId={id} departureDate={request.departureDate} returnDate={request.returnDate} showStatus />
        </Card>
      )}
      {activeTab === 'notes'     && <TourNotes request={request} legacyNotes={request.internalNotes || ''} readOnly />}
    </div>
  );
}
